import store from '../../state';
import { SolarLunarService } from '../../services/SolarLunarService';
import { getMoonPhaseDetails, calculateLongTermDarkSkiesScore } from '../../utils/astronomy';
import { getBortleAt } from '../../services/PoiDataService';
import * as SunCalc from 'suncalc';
import { SolarLunarDockPanel } from './SolarLunarDockPanel';

export class SolarLunarPanel {
  private currentYear: number;
  private currentMonth: number; // 0 - 11
  private baseYear: number;
  private baseMonth: number;
  private currentBortle: number = 4;
  private lastPoiKey: string = '';
  private onClose?: () => void;
  private isCalendarCollapsed: boolean = false;
  private isSolarTimesCollapsed: boolean = false;

  constructor() {
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.currentMonth = now.getMonth();
    this.baseYear = now.getFullYear();
    this.baseMonth = now.getMonth();

    try {
      this.isCalendarCollapsed = localStorage.getItem('gw_solarlunar_calendar_collapsed') === 'true';
      this.isSolarTimesCollapsed = localStorage.getItem('gw_solarlunar_times_collapsed') === 'true';
    } catch (_) {}
  }

  public setOnClose(cb: () => void) {
    this.onClose = cb;
  }

  public render(state: any): string {
    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const tz = state.userTimezone || 'America/Los_Angeles';

    if (state.solarLunarBortle !== undefined) {
      this.currentBortle = state.solarLunarBortle;
    }

    const locKey = `${lat.toFixed(3)}_${lon.toFixed(3)}`;
    if (locKey && locKey !== this.lastPoiKey) {
      this.lastPoiKey = locKey;
      getBortleAt(lat, lon).then(bortleVal => {
        if (bortleVal > 0 && bortleVal !== store.getState().solarLunarBortle) {
          store.update({ solarLunarBortle: bortleVal });
        }
      });
    }

    // Calculate month & year from solarLunarMonthOffset relative to base Month for 12-month limit
    const monthsFromBase = state.solarLunarMonthOffset !== undefined ? state.solarLunarMonthOffset : 0;
    const canPrev = monthsFromBase > 0;
    const canNext = monthsFromBase < 11; // Up to +11 months (12 months total including current)

    const totalMonths = this.baseMonth + monthsFromBase;
    this.currentYear = this.baseYear + Math.floor(totalMonths / 12);
    this.currentMonth = ((totalMonths % 12) + 12) % 12;

    const dateObj = new Date(this.currentYear, this.currentMonth, 1);
    const monthName = dateObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const selectedDate = state.solarLunarSelectedDate || new Date();
    let selectedDateStr = '';
    if (selectedDate instanceof Date) {
      try {
        const dFmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
        selectedDateStr = dFmt.format(selectedDate);
      } catch (e) {
        selectedDateStr = selectedDate.toISOString().split('T')[0];
      }
    } else {
      selectedDateStr = String(selectedDate).split('T')[0];
    }

    const days = SolarLunarService.getMonthCalendar(
      this.currentYear,
      this.currentMonth,
      lat,
      lon,
      this.currentBortle,
      selectedDateStr,
      tz
    );

    const firstDayIndex = new Date(this.currentYear, this.currentMonth, 1).getDay(); // 0 = Sun
    const monthHeaderDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    let calendarGridHtml = `
      <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; font-size: 0.68rem; text-align: center; font-weight: 700; color: var(--text-muted); margin-bottom: 4px; width: 100%; box-sizing: border-box;">
        ${monthHeaderDays.map(d => `<div style="min-width: 0; box-sizing: border-box; text-align: center;">${d}</div>`).join('')}
      </div>
      <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; width: 100%; box-sizing: border-box;">
    `;

    // Blank padding cells
    for (let i = 0; i < firstDayIndex; i++) {
      calendarGridHtml += `<div style="height: 38px; min-width: 0; background: rgba(255,255,255,0.02); border-radius: 4px; box-sizing: border-box;"></div>`;
    }

    // Day cells
    days.forEach(day => {
      let bg = 'rgba(15, 23, 42, 0.6)';
      let border = '1px solid rgba(255,255,255,0.08)';

      if (day.isSelected) {
        bg = 'rgba(56, 189, 248, 0.25)';
        border = '1.5px solid #38bdf8';
      } else if (day.isToday) {
        border = '1.5px solid #f59e0b';
      }

      let badgeHtml = '';
      if (day.isSupermoon) {
        badgeHtml = `<span title="Supermoon" style="position: absolute; top: 1px; right: 1px; font-size: 0.50rem;">🌟</span>`;
      } else if (day.isHarvestMoon) {
        badgeHtml = `<span title="Harvest Moon" style="position: absolute; top: 1px; right: 1px; font-size: 0.50rem;">🌾</span>`;
      } else if (day.isMoonlessNight) {
        badgeHtml = `<span title="Moonless Night (Prime Dark Skies)" style="position: absolute; top: 1px; right: 1px; font-size: 0.50rem;">🌌</span>`;
      }

      calendarGridHtml += `
        <div class="solar-lunar-day-cell ${day.isSelected ? 'selected' : ''}" data-date="${day.dateStr}" style="position: relative; height: 32px; min-width: 0; box-sizing: border-box; background: ${bg}; border: ${border}; border-radius: 5px; padding: 2px 3px; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; transition: all 0.15s ease;">
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.60rem; font-weight: 700; color: ${day.isSelected ? '#38bdf8' : '#e2e8f0'}; min-width: 0;">
            <span style="line-height: 1;">${day.dayNumber}</span>
            <span style="font-size: 0.68rem; line-height: 1;">${day.moonIcon}</span>
          </div>
          ${badgeHtml}
          <div style="font-size: 0.52rem; color: #94a3b8; font-weight: 600; text-align: right; line-height: 1;">
            ★ ${day.darkSkiesScore}
          </div>
        </div>
      `;
    });

    calendarGridHtml += `</div>`;

    // Detailed day summary card for selectedDate
    const sTimes = SunCalc.getTimes(selectedDate, lat, lon);
    const mTimes = SunCalc.getMoonTimes(selectedDate, lat, lon);
    const mDetails = getMoonPhaseDetails(selectedDate);

    const fmtT = (d?: Date | null) => (!d || isNaN(d.getTime()) ? '--' : d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }));

    const getCompassDir = (deg: number): string => {
      const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
      return dirs[Math.round(deg / 45) % 8];
    };

    const getSunAngleInfo = (d?: Date | null) => {
      if (!d || isNaN(d.getTime())) return '';
      const pos = SunCalc.getPosition(d, lat, lon);
      const azDeg = Math.round(((pos.azimuth % 360) + 360) % 360);
      return ` (${azDeg}° ${getCompassDir(azDeg)})`;
    };

    const getMoonAngleInfo = (d?: Date | null) => {
      if (!d || isNaN(d.getTime())) return '';
      const pos = SunCalc.getMoonPosition(d, lat, lon);
      const azDeg = Math.round(((pos.azimuth % 360) + 360) % 360);
      return ` (${azDeg}° ${getCompassDir(azDeg)})`;
    };

    const sunrise = fmtT(sTimes.sunrise);
    const sunset = fmtT(sTimes.sunset);
    const moonrise = fmtT(mTimes.rise);
    const moonset = fmtT(mTimes.set);

    const goldenHour = `${fmtT(sTimes.goldenHour)} - ${fmtT(sTimes.sunset)}`;
    const blueHour = `${fmtT(sTimes.sunset)} - ${fmtT(sTimes.dusk)}`;

    const darkSkiesRating = calculateLongTermDarkSkiesScore(this.currentBortle, selectedDate, lat, lon);

    return `
      <div id="gw-solarlunar-container" class="metric-card gw-solarlunar-calendar" style="display: flex; flex-direction: column; gap: 6px; background: rgba(0,0,0,0.45); border: 1px solid rgba(99, 102, 241, 0.4); padding: 8px; border-radius: 8px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <h2 style="font-size: 0.76rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #a5b4fc; margin: 0; display: flex; align-items: center; gap: 6px;">
            🌗 Solar & Lunar Tools
          </h2>
          <div style="display: flex; align-items: center; gap: 4px;">
            <button class="legend-clear-layer-btn" data-layer="solar_lunar_tools" title="Clear this layer" style="font-size: 0.64rem; padding: 2px 6px;">
              🗑️ Clear Layer
            </button>
            <span style="font-size: 0.60rem; color: #38bdf8; font-weight: 700; background: rgba(56, 189, 248, 0.15); padding: 1px 5px; border-radius: 3px; border: 1px solid rgba(56, 189, 248, 0.3);">
              365-Day Ephemeris
            </span>
            <button id="btn-solar-lunar-close" title="Close Solar & Lunar Tools" style="background: transparent; border: none; color: #94a3b8; font-size: 1rem; cursor: pointer; padding: 0 2px; line-height: 1;">&times;</button>
          </div>
        </div>

        ${!poi ? `
          <div style="font-size: 0.68rem; color: #f59e0b; background: rgba(245, 158, 11, 0.12); border: 1px dashed rgba(245, 158, 11, 0.35); padding: 4px 6px; border-radius: 4px; display: flex; align-items: center; gap: 4px;">
            📍 <span>Click anywhere on the map to set a precise Point of Interest.</span>
          </div>
        ` : ''}

        <!-- Methodology Disclaimer Note -->
        <div style="font-size: 0.65rem; color: #94a3b8; background: rgba(56, 189, 248, 0.08); border: 1px solid rgba(56, 189, 248, 0.2); padding: 4px 6px; border-radius: 4px; display: flex; align-items: flex-start; gap: 4px; line-height: 1.3;">
          <span style="font-size: 0.75rem; flex-shrink: 0;">ℹ️</span>
          <span>Outlook based on static Bortle and solar/lunar phases, no atmospheric data. Intended for long term outlook/planning.</span>
        </div>

        <!-- Open Full-Width Calendar Dock Button -->
        <button type="button" class="btn-open-solar-lunar-dock" style="display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; padding: 6px 10px; background: linear-gradient(135deg, rgba(56, 189, 248, 0.25) 0%, rgba(99, 102, 241, 0.15) 100%); border: 1px solid rgba(56, 189, 248, 0.45); border-radius: 6px; color: #f8fafc; font-size: 0.74rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease;">
          <span>📈</span>
          <span>Open Full-Width Ephemeris Calendar Dock</span>
        </button>

        <!-- Month Navigation & Calendar Collapsible Header -->
        <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(15, 23, 42, 0.6); padding: 4px 8px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.08);">
          <div id="solar-lunar-cal-toggle-header" style="display: flex; align-items: center; gap: 5px; cursor: pointer; user-select: none;" title="Toggle Calendar">
            <button type="button" id="btn-solar-lunar-toggle-calendar" title="${this.isCalendarCollapsed ? 'Expand Calendar' : 'Collapse Calendar'}" style="background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #94a3b8; width: 17px; height: 17px; border-radius: 3px; display: inline-flex; align-items: center; justify-content: center; font-size: 0.58rem; cursor: pointer; padding: 0; line-height: 1;">
              <span id="arrow-solar-lunar-calendar" style="display: inline-block; transform: ${this.isCalendarCollapsed ? 'rotate(0deg)' : 'rotate(90deg)'}; transition: transform 0.2s;">▶</span>
            </button>
            <span id="label-solar-lunar-toggle-calendar" style="font-size: 0.72rem; font-weight: 700; color: #f8fafc;">
              📅 ${monthName}
            </span>
          </div>
          <div id="solar-lunar-month-nav" style="display: flex; align-items: center; gap: 3px; ${this.isCalendarCollapsed ? 'display: none;' : ''}">
            <button id="btn-solar-lunar-prev-month" ${!canPrev ? 'disabled' : ''} style="background: transparent; border: none; color: ${canPrev ? '#38bdf8' : '#475569'}; font-size: 0.68rem; font-weight: 700; cursor: ${canPrev ? 'pointer' : 'default'}; padding: 1px 4px;">
              ◀ Prev
            </button>
            <button id="btn-solar-lunar-next-month" ${!canNext ? 'disabled' : ''} style="background: transparent; border: none; color: ${canNext ? '#38bdf8' : '#475569'}; font-size: 0.68rem; font-weight: 700; cursor: ${canNext ? 'pointer' : 'default'}; padding: 1px 4px;">
              Next ▶
            </button>
          </div>
        </div>

        <!-- 365-Day Month Calendar Grid (Collapsible) -->
        <div id="solar-lunar-calendar-body" style="${this.isCalendarCollapsed ? 'display: none;' : ''}">
          ${calendarGridHtml}
        </div>

        <!-- Selected Date Summary Card (Collapsible Solar & Lunar Times) -->
        <div style="background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(99, 102, 241, 0.3); border-radius: 6px; padding: 6px 8px; display: flex; flex-direction: column; gap: 5px;">
          <div id="solar-lunar-times-header" style="display: flex; justify-content: space-between; align-items: center; cursor: pointer; user-select: none; ${this.isSolarTimesCollapsed ? '' : 'border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 4px;'}" title="Toggle Solar & Lunar Times">
            <div style="display: flex; align-items: center; gap: 5px;">
              <button type="button" id="btn-solar-lunar-toggle-times" title="${this.isSolarTimesCollapsed ? 'Expand Solar & Lunar Times' : 'Collapse Solar & Lunar Times'}" style="background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #94a3b8; width: 17px; height: 17px; border-radius: 3px; display: inline-flex; align-items: center; justify-content: center; font-size: 0.58rem; cursor: pointer; padding: 0; line-height: 1;">
                <span id="arrow-solar-lunar-times" style="display: inline-block; transform: ${this.isSolarTimesCollapsed ? 'rotate(0deg)' : 'rotate(90deg)'}; transition: transform 0.2s;">▶</span>
              </button>
              <span style="font-size: 0.72rem; font-weight: 700; color: #f1f5f9;">
                🗓️ ${selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <span style="font-size: 0.65rem; color: #34d399; font-weight: 700;">
              Bortle ${this.currentBortle} • ${darkSkiesRating.score}/10 (${darkSkiesRating.label})
            </span>
          </div>

          <div id="solar-lunar-times-body" style="display: ${this.isSolarTimesCollapsed ? 'none' : 'grid'}; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 0.68rem; line-height: 1.3;">
            <div style="display: flex; flex-direction: column; gap: 2px;">
              <div style="color: #f97316; font-weight: 700; margin-bottom: 1px;">🌅 Solar Times</div>
              <div>Sunrise: <strong>${sunrise}</strong><span style="font-size: 0.61rem; color: #cbd5e1;">${getSunAngleInfo(sTimes.sunrise)}</span></div>
              <div>Sunset: <strong>${sunset}</strong><span style="font-size: 0.61rem; color: #cbd5e1;">${getSunAngleInfo(sTimes.sunset)}</span></div>
              <div>Golden Hour: <strong>${goldenHour}</strong></div>
              <div>Blue Hour: <strong>${blueHour}</strong></div>
            </div>
            <div style="display: flex; flex-direction: column; gap: 2px;">
              <div style="color: #38bdf8; font-weight: 700; margin-bottom: 1px;">🌙 Lunar (${mDetails.icon} ${mDetails.illumination})</div>
              <div>Moonrise: <strong>${moonrise}</strong><span style="font-size: 0.61rem; color: #cbd5e1;">${getMoonAngleInfo(mTimes.rise)}</span></div>
              <div>Moonset: <strong>${moonset}</strong><span style="font-size: 0.61rem; color: #cbd5e1;">${getMoonAngleInfo(mTimes.set)}</span></div>
              <div>Phase: <strong>${mDetails.name}</strong></div>
            </div>
          </div>
        </div>

        <!-- Vector Toggle Options -->
        <div style="display: flex; flex-direction: column; gap: 4px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 5px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 0.66rem; font-weight: 700; color: #818cf8; text-transform: uppercase; letter-spacing: 0.5px;">
              🧭 Map Angle & Vector Overlays
            </span>
            ${!poi ? `<span style="font-size: 0.62rem; color: #f59e0b; font-weight: 600;">(Select POI on map to enable)</span>` : ''}
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 5px; font-size: 0.68rem; ${!poi ? 'opacity: 0.55;' : ''}">
            <label style="display: flex; align-items: center; gap: 4px; color: var(--text-primary); cursor: ${poi ? 'pointer' : 'not-allowed'}; font-weight: 600;">
              <input type="checkbox" id="chk-solar-lunar-sun-vectors" ${state.showSunAngles && poi ? 'checked' : ''} ${!poi ? 'disabled' : ''} style="cursor: ${poi ? 'pointer' : 'not-allowed'}; accent-color: #f97316;">
              <span>🌅 Sun Angles</span>
            </label>
            <label style="display: flex; align-items: center; gap: 4px; color: var(--text-primary); cursor: ${poi ? 'pointer' : 'not-allowed'}; font-weight: 600;">
              <input type="checkbox" id="chk-solar-lunar-moon-vectors" ${state.showMoonAngles && poi ? 'checked' : ''} ${!poi ? 'disabled' : ''} style="cursor: ${poi ? 'pointer' : 'not-allowed'}; accent-color: #38bdf8;">
              <span>🌙 Moon Angles</span>
            </label>
            <label style="display: flex; align-items: center; gap: 4px; color: var(--text-primary); cursor: ${poi ? 'pointer' : 'not-allowed'}; font-weight: 600;">
              <input type="checkbox" id="chk-solar-lunar-mw-line" ${state.milkyWayLineEnabled && poi ? 'checked' : ''} ${!poi ? 'disabled' : ''} style="cursor: ${poi ? 'pointer' : 'not-allowed'}; accent-color: #6366f1;">
              <span>🌌 Milky Way Core</span>
            </label>
            <label style="display: flex; align-items: center; gap: 4px; color: var(--text-primary); cursor: pointer; font-weight: 600;">
              <input type="checkbox" id="chk-solar-lunar-bortle-layer" ${state.bortleOverlayEnabled ? 'checked' : ''} style="cursor: pointer; accent-color: #a855f7;">
              <span>✨ Light Pollution (Bortle)</span>
            </label>
            <label style="display: flex; align-items: center; gap: 4px; color: var(--text-primary); cursor: ${poi ? 'pointer' : 'not-allowed'}; font-weight: 600;">
              <input type="checkbox" id="chk-solar-lunar-hillshade" ${state.solarLunarHillshadeEnabled && poi ? 'checked' : ''} ${!poi ? 'disabled' : ''} style="cursor: ${poi ? 'pointer' : 'not-allowed'}; accent-color: #eab308;">
              <span>🏔️ Sun/Moon Light Shading</span>
            </label>
          </div>

          <button id="btn-solar-lunar-launch-ar" ${!poi ? 'disabled' : ''} style="margin-top: 5px; padding: 6px 10px; background: ${poi ? 'linear-gradient(135deg, #f97316 0%, #38bdf8 100%)' : 'rgba(255,255,255,0.08)'}; color: ${poi ? '#ffffff' : '#64748b'}; border: none; border-radius: 5px; font-weight: 700; font-size: 0.70rem; cursor: ${poi ? 'pointer' : 'not-allowed'}; box-shadow: ${poi ? '0 2px 6px rgba(249,115,22,0.25)' : 'none'}; display: flex; align-items: center; justify-content: center; gap: 5px; transition: all 0.15s ease;">
            ☀️🕶️ Launch Sun & Moon 3D AR View
          </button>
        </div>
      </div>
    `;

  }

  public bindEvents(overlay: HTMLElement, onDateSelect: (d: Date) => void) {
    // Close Button
    const closeBtn = overlay.querySelector('#btn-solar-lunar-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.onClose) {
          this.onClose();
        }
      });
    }

    // Toggle Calendar Collapse
    const toggleCalHeader = overlay.querySelector('#solar-lunar-cal-toggle-header');
    if (toggleCalHeader) {
      toggleCalHeader.addEventListener('click', (e) => {
        e.stopPropagation();
        this.isCalendarCollapsed = !this.isCalendarCollapsed;
        try {
          localStorage.setItem('gw_solarlunar_calendar_collapsed', String(this.isCalendarCollapsed));
        } catch (_) {}
        const body = overlay.querySelector('#solar-lunar-calendar-body') as HTMLElement | null;
        if (body) {
          body.style.display = this.isCalendarCollapsed ? 'none' : 'block';
        }
        const nav = overlay.querySelector('#solar-lunar-month-nav') as HTMLElement | null;
        if (nav) {
          nav.style.display = this.isCalendarCollapsed ? 'none' : 'flex';
        }
        const arrow = overlay.querySelector('#arrow-solar-lunar-calendar') as HTMLElement | null;
        if (arrow) {
          arrow.style.transform = this.isCalendarCollapsed ? 'rotate(0deg)' : 'rotate(90deg)';
        }
        const btnToggle = overlay.querySelector('#btn-solar-lunar-toggle-calendar') as HTMLElement | null;
        if (btnToggle) {
          btnToggle.title = this.isCalendarCollapsed ? 'Expand Calendar' : 'Collapse Calendar';
        }
        const mapOverlay = document.getElementById('map-legend-overlay');
        if (mapOverlay && typeof (mapOverlay as any)._updateDynamicHeight === 'function') {
          (mapOverlay as any)._updateDynamicHeight();
        }
      });
    }

    // Toggle Solar & Lunar Times Collapse
    const toggleTimesHeader = overlay.querySelector('#solar-lunar-times-header');
    if (toggleTimesHeader) {
      toggleTimesHeader.addEventListener('click', (e) => {
        e.stopPropagation();
        this.isSolarTimesCollapsed = !this.isSolarTimesCollapsed;
        try {
          localStorage.setItem('gw_solarlunar_times_collapsed', String(this.isSolarTimesCollapsed));
        } catch (_) {}
        const body = overlay.querySelector('#solar-lunar-times-body') as HTMLElement | null;
        if (body) {
          body.style.display = this.isSolarTimesCollapsed ? 'none' : 'grid';
        }
        const arrow = overlay.querySelector('#arrow-solar-lunar-times') as HTMLElement | null;
        if (arrow) {
          arrow.style.transform = this.isSolarTimesCollapsed ? 'rotate(0deg)' : 'rotate(90deg)';
        }
        const btnToggle = overlay.querySelector('#btn-solar-lunar-toggle-times') as HTMLElement | null;
        if (btnToggle) {
          btnToggle.title = this.isSolarTimesCollapsed ? 'Expand Solar & Lunar Times' : 'Collapse Solar & Lunar Times';
        }
        if (toggleTimesHeader instanceof HTMLElement) {
          if (this.isSolarTimesCollapsed) {
            toggleTimesHeader.style.borderBottom = 'none';
            toggleTimesHeader.style.paddingBottom = '0';
          } else {
            toggleTimesHeader.style.borderBottom = '1px solid rgba(255,255,255,0.08)';
            toggleTimesHeader.style.paddingBottom = '4px';
          }
        }
        const mapOverlay = document.getElementById('map-legend-overlay');
        if (mapOverlay && typeof (mapOverlay as any)._updateDynamicHeight === 'function') {
          (mapOverlay as any)._updateDynamicHeight();
        }
      });
    }

    // Prev Month Button
    const prevBtn = overlay.querySelector('#btn-solar-lunar-prev-month');
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const currentOffset = store.getState().solarLunarMonthOffset !== undefined ? store.getState().solarLunarMonthOffset : 0;
        if (currentOffset > 0) {
          store.update({ solarLunarMonthOffset: currentOffset - 1 });
        }
      });
    }

    // Next Month Button
    const nextBtn = overlay.querySelector('#btn-solar-lunar-next-month');
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const currentOffset = store.getState().solarLunarMonthOffset !== undefined ? store.getState().solarLunarMonthOffset : 0;
        if (currentOffset < 11) {
          store.update({ solarLunarMonthOffset: currentOffset + 1 });
        }
      });
    }

    // Day Cells Click
    overlay.querySelectorAll('.solar-lunar-day-cell').forEach(cell => {
      cell.addEventListener('click', (e) => {
        e.stopPropagation();
        const dateStr = (cell as HTMLElement).dataset.date;
        if (!dateStr) return;
        const [y, m, d] = dateStr.split('-').map(Number);
        const selectedDate = new Date(y, m - 1, d, 12, 0, 0);
        onDateSelect(selectedDate);
      });
    });

    // Sun Vectors Checkbox
    const sunChk = overlay.querySelector('#chk-solar-lunar-sun-vectors') as HTMLInputElement;
    if (sunChk) {
      sunChk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ showSunAngles: sunChk.checked });
      });
    }

    // Moon Vectors Checkbox
    const moonChk = overlay.querySelector('#chk-solar-lunar-moon-vectors') as HTMLInputElement;
    if (moonChk) {
      moonChk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ showMoonAngles: moonChk.checked });
      });
    }

    // Milky Way Checkbox
    const mwChk = overlay.querySelector('#chk-solar-lunar-mw-line') as HTMLInputElement;
    if (mwChk) {
      mwChk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ milkyWayLineEnabled: mwChk.checked });
      });
    }

    // Bortle Light Pollution Layer Checkbox
    const bortleChk = overlay.querySelector('#chk-solar-lunar-bortle-layer') as HTMLInputElement;
    if (bortleChk) {
      bortleChk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ bortleOverlayEnabled: bortleChk.checked });
      });
    }

    // Sun/Moon Light Shading (Hillshade) Checkbox
    const hillshadeChk = overlay.querySelector('#chk-solar-lunar-hillshade') as HTMLInputElement;
    if (hillshadeChk) {
      hillshadeChk.addEventListener('change', (e) => {
        e.stopPropagation();
        const isEnabled = hillshadeChk.checked;
        const updates: any = { solarLunarHillshadeEnabled: isEnabled };
        if (isEnabled) {
          updates.terrain3dEnabled = true;
        }
        store.update(updates);
      });
    }

    // Launch Full-Width Ephemeris Calendar Dock Button
    const openDockBtn = overlay.querySelector('.btn-open-solar-lunar-dock');
    if (openDockBtn) {
      openDockBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        SolarLunarDockPanel.getInstance().open();
      });
    }

    // Launch Sun & Moon 3D AR View Button
    const launchArBtn = overlay.querySelector('#btn-solar-lunar-launch-ar');
    if (launchArBtn) {
      launchArBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const state = store.getState();
        if (state.selectedPoi) {
          const poi = state.selectedPoi;
          const targetDate = state.solarLunarSelectedDate || new Date();
          const tz = state.userTimezone || 'America/Los_Angeles';
          const sTimes = SunCalc.getTimes(targetDate, poi.lat, poi.lng);
          let hourDec = 7.33; // 7:20 AM fallback
          if (sTimes.sunrise && !isNaN(sTimes.sunrise.getTime())) {
            const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false });
            const parts = dFmt.formatToParts(sTimes.sunrise);
            const sHour = parseInt(parts.find(p => p.type === 'hour')?.value || '7', 10);
            const sMin = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
            hourDec = sHour + (sMin + 20) / 60;
          }

          store.update({
            sunMoonArEnabled: true,
            arTargetMode: 'sun',
            arPerspectiveMode: 'observer',
            arSelectedDate: targetDate,
            arSelectedHour: hourDec
          });
        }
      });
    }
  }
}
